import { IdentityHttpError } from './identity-http';
import type { PanelLoginMembershipChoice, PanelLoginMembershipSelection } from '../foundation/runtime-port';
import type { PanelCredentialSession, PanelRuntimeSession, RuntimeTenant } from './session-model';

// The states of a staff session as the Identity runtime commits them, and how a transport failure reads in them.

export type RuntimeFailureReason = 'network' | 'permission' | 'server' | 'invalid_response';
/** `unassigned`: Identity refused the workspace read
 * because this non-manager member has no Servicio/Cocina work assignment and
 * no department placement. It is an explanation, never a grant. */
export type RuntimeUnavailableReason = RuntimeFailureReason | 'unassigned';
export type RuntimeOperation = 'hydrate' | 'renew' | 'logout';
export type SessionIdentity = PanelRuntimeSession | PanelCredentialSession;
type CredentialRotationState = Readonly<{ kind: 'credential_required'; credential: PanelCredentialSession }> & (
  | Readonly<{ status: 'required' | 'expired' }>
  | Readonly<{ status: 'unavailable'; reason: RuntimeFailureReason; operation: RuntimeOperation }>
);
export type IdentityRuntimeState =
  | { readonly kind: 'signed_out'; readonly localOnly?: true }
  | { readonly kind: 'restoring' }
  | { readonly kind: 'membership_selection_required'; readonly personName: PanelLoginMembershipSelection['person_name'];
      readonly memberships: readonly PanelLoginMembershipChoice[] }
  | { readonly kind: 'ready' | 'expired'; readonly session: PanelRuntimeSession }
  | CredentialRotationState
  | { readonly kind: 'unavailable'; readonly reason: RuntimeUnavailableReason; readonly session: PanelRuntimeSession | null; readonly operation: RuntimeOperation;
      readonly verifiedCredentialScope?: { readonly principalId: string; readonly tenant: ExpectedTenant } };

/** Form-owned, ephemeral input. The runtime stores neither credential nor PIN. */
export interface CredentialRotationRequest {
  readonly currentCredential: string;
  readonly newPin: string;
  readonly idempotencyKey: string;
}
export type CredentialRotationOutcome =
  | Readonly<{ kind: 'complete' }>
  | Readonly<{ kind: 'verification_required' }>
  | Readonly<{ kind: 'superseded' }>
  | Readonly<{ kind: 'refused'; reason: 'invalid_request' | 'authorization_denied' | 'conflict' | 'too_many_attempts' }>
  | Readonly<{ kind: 'uncertain'; reason: RuntimeFailureReason }>
  | Readonly<{ kind: 'unavailable'; reason: RuntimeFailureReason | 'verification_pending' }>;

export const tokenValue = (value: string): string => {
  const token = value.replace(/^Bearer\s+/i, '').trim();
  if (!token || /\s/.test(token)) throw new IdentityHttpError('invalid_response');
  return token;
};
export const reasonFor = (error: unknown): RuntimeFailureReason => error instanceof IdentityHttpError
  ? error.kind === 'network' ? 'network' : error.kind === 'permission' ? 'permission'
    : error.kind === 'invalid_response' || error.kind === 'invalid_request' ? 'invalid_response' : 'server'
  : 'network';
export const isUnauthorized = (error: unknown): boolean => error instanceof IdentityHttpError && error.kind === 'unauthenticated';
export const isInvalidResponse = (error: unknown): boolean => error instanceof IdentityHttpError && error.kind === 'invalid_response';
/** Screen callers see an unplaced member's state as the permission refusal it is. */
export const refusalKind = (reason: RuntimeUnavailableReason): RuntimeFailureReason => reason === 'unassigned' ? 'permission' : reason;
/** The server's own code on a validated 403 body; the Panel never infers it from roles. */
export const isWorkspacePlacementMissing = (error: unknown): boolean => {
  if (!(error instanceof IdentityHttpError) || error.status !== 403) return false;
  const errors = error.canonical?.errors;
  return typeof errors === 'object' && errors !== null && !Array.isArray(errors)
    && (errors as { readonly errorCode?: unknown }).errorCode === 'workspace_placement_missing';
};
export type ExpectedTenant = { readonly kind: RuntimeTenant['kind']; readonly id?: string };
/** The session a state hands screens, whose grants a remembered board was read under. */
export const screenSession = (state: IdentityRuntimeState): PanelRuntimeSession | null =>
  state.kind === 'ready' || state.kind === 'expired' || state.kind === 'unavailable' ? state.session : null;
